import { useSearchParams, useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import { useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import SurfaceCard from 'components/design-system/SurfaceCard'
import LoaderDots from 'components/design-system/LoaderDots'
import BorderLogo from 'components/design-system/BorderLogo'
import decorationDots from './decoration-dots.svg'
import iconClose from './icon-close.svg'
import store from 'store/store'
import { storeAccessToken } from 'utils/authToken'
import { establishSession } from 'utils/authSession'

const LOADER_STEPS = 6
const LOADER_STEP_MS = 300

const titleSx = {
  position: 'relative',
  fontFamily: 'var(--font-family-primary)',
  fontWeight: 'var(--font-weight-bold)',
  fontSize: '64px',
  lineHeight: 'normal',
  color: 'var(--color-text-primary)',
  whiteSpace: 'nowrap'
}

const RIBBON_REPEAT = 10

const ribbonTextSx = {
  fontFamily: 'var(--font-family-primary)',
  fontWeight: 'var(--font-weight-bold)',
  fontSize: 'var(--font-size-16)',
  lineHeight: 'normal',
  color: 'var(--color-status-warning-fg)',
  whiteSpace: 'nowrap',
  flexShrink: 0
}

/** Dải băng chéo "You don't have permission" (Figma 126:24446 / 126:24456). Tâm dải đặt theo % khung 1440×880. */
const PermissionRibbon = ({ left, top, rotate }) => (
  <Box
    aria-hidden
    sx={{
      position: 'absolute',
      left,
      top,
      transform: `translate(-50%, -50%) rotate(${rotate})`,
      display: 'flex',
      alignItems: 'center',
      gap: '48px',
      px: '48px',
      py: '20px',
      bgcolor: 'var(--color-status-warning-bg)',
      borderTop: 'var(--stroke-default) solid var(--color-status-warning-border)',
      borderBottom: 'var(--stroke-default) solid var(--color-status-warning-border)',
      pointerEvents: 'none'
    }}
  >
    {Array.from({ length: RIBBON_REPEAT }, (_, i) => (
      <Box key={i} sx={{ display: 'contents' }}>
        <Typography sx={ribbonTextSx}>You don&rsquo;t have permission</Typography>
        <Box component="img" src={iconClose} alt="" sx={{ display: 'block', width: 20, height: 20, flexShrink: 0 }} />
      </Box>
    ))}
  </Box>
)

/** Entry từ hệ thống ngoài: `/landing?state=<base64{token,source}>` → bearer → app. */
const EntryLoadingPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const rawState = searchParams.get('state') ?? ''
  const [loading, setLoading] = useState(true)
  const [step, setStep] = useState(1)
  const navigate = useNavigate()

  useEffect(() => {
    if (!rawState) {
      setLoading(false)
      return
    }

    let cancelled = false

    const run = async () => {
      try {
        const { token, source } = JSON.parse(atob(rawState))

        if (!token) throw new Error('Missing token in state')

        storeAccessToken(token)

        if (source === 'NBSS') {
          await establishSession(store.dispatch)
        }

        if (cancelled) return

        setSearchParams({}, { replace: true })
        navigate('/navigator', { replace: true })
      } catch (error) {
        console.error('Entry state verification failed:', error)
        if (!cancelled) setLoading(false)
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [rawState, navigate, setSearchParams])

  useEffect(() => {
    if (!loading) return
    const timer = setInterval(() => setStep((s) => (s % LOADER_STEPS) + 1), LOADER_STEP_MS)
    return () => clearInterval(timer)
  }, [loading])

  return (
    <Box
      component="main"
      data-entry-state-present={rawState ? 'true' : 'false'}
      sx={{
        position: 'relative',
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        p: 'var(--space-32)',
        bgcolor: 'var(--color-bg-app)',
        opacity: 0.87
      }}
    >
      <SurfaceCard sx={{ flex: '1 0 0', minHeight: 0, width: '100%' }}>
        <Box
          sx={{
            position: 'relative',
            flex: '1 0 0',
            minHeight: 0,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 'var(--space-16)',
            p: 'var(--space-32)',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              bottom: 0,
              width: '1311px',
              height: '699px',
              opacity: 0.04,
              overflow: 'hidden',
              pointerEvents: 'none'
            }}
          >
            <Box sx={{ position: 'absolute', inset: '0 0 0.11% 0' }}>
              <Box component="img" src={decorationDots} alt="" sx={{ display: 'block', width: '100%', height: '100%' }} />
            </Box>
          </Box>

          <Box
            sx={{
              position: 'relative',
              flex: '1 0 0',
              minHeight: 0,
              width: '100%',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              overflow: 'hidden'
            }}
          >
            {loading ? (
              <Typography component="h1" sx={titleSx}>
                WELCOME TO...
              </Typography>
            ) : (
              <Typography component="h1" sx={titleSx}>
                OOPS !
                <br />
                LOADING FAIL
              </Typography>
            )}
            <BorderLogo width={loading ? 117.6 : 133.636} height={147} />
          </Box>

          {loading && <LoaderDots step={step} sx={{ position: 'relative', flexShrink: 0 }} />}
        </Box>
      </SurfaceCard>

      {!loading && (
        <>
          <PermissionRibbon left="52.78%" top="63.64%" rotate="12deg" />
          <PermissionRibbon left="47.22%" top="68.18%" rotate="-8deg" />
        </>
      )}
    </Box>
  )
}

PermissionRibbon.propTypes = {
  left: PropTypes.string.isRequired,
  top: PropTypes.string.isRequired,
  rotate: PropTypes.string.isRequired
}

export default EntryLoadingPage
